import React, { useState, useMemo, useEffect } from "react";
import { Icon } from "../../UIElements/Icon";
import Modal from "../../UIElements/Modal";
import WidgetTypeCard from "./WidgetTypeCard";
import WidgetConfigurator from "./WidgetConfigurator";
import WidgetCodeSnippet from "./WidgetCodeSnippet";
import WidgetPreview from "./WidgetPreview";
import { getFinitlessChatSiteUrl } from "../../../../lib/linkInBio";

const WIDGET_TYPES = [
  {
    id: "floating",
    name: "Floating Chat",
    description:
      "WhatsApp-style floating button that stays visible in the corner of the screen.",
    icon: "MessageCircle",
    popular: true,
    features: ["Always visible", "Smooth animations", "Fullscreen on mobile"],
    enabled: true,
  },
  {
    id: "popup",
    name: "Popup Modal",
    description: "Opens in a centered modal window when clicking a button.",
    icon: "Maximize2",
    popular: false,
    features: ["Centered modal", "Elegant overlay"],
    enabled: true,
  },
  {
    id: "inline",
    name: "Inline (Embedded)",
    description: "Integrates directly into the page content.",
    icon: "Layout",
    popular: false,
    features: ["No popups", "Integrated in flow", "Customizable height"],
    enabled: false,
  },
];

const getAgentChatUrl = (agent) => getFinitlessChatSiteUrl(agent?.site) ?? "";

const WidgetConfigModal = ({ isOpen, onClose, agent }) => {
  const [selectedType, setSelectedType] = useState("floating");
  const [config, setConfig] = useState({
    chatUrl: "",
    primaryColor: "#00BFFF",
    secondaryColor: "#FF00FF",
    position: "bottom-right",
    buttonSize: 50,
    chatWidth: 400,
    chatHeight: 650,
    showHeader: true,
    iconUrl: "",
  });
  const [activeTab, setActiveTab] = useState("configure");

  useEffect(() => {
    if (agent) {
      const chatUrl = getAgentChatUrl(agent);
      setConfig((prev) => ({ ...prev, chatUrl }));
    }
  }, [agent]);

  const handleConfigChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const selectedWidget = useMemo(
    () => WIDGET_TYPES.find((w) => w.id === selectedType),
    [selectedType],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Widget Configuration"
      subtitle="Generate embeddable code for this AI agent"
      maxWidth="max-w-6xl"
    >
      <div className="space-y-6">
        {/* Integration Instructions */}
        <div className="bg-card border-border rounded-xl border p-6">
          <h3 className="text-card-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
            <Icon name="HelpCircle" className="text-brand-magenta h-5 w-5" />
            Steps to configure your widget
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex gap-4">
              <div className="bg-brand-cyan/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                <span className="text-brand-cyan font-bold">1</span>
              </div>
              <div>
                <h4 className="text-card-foreground mb-1 font-medium">
                  Configure your widget
                </h4>
                <p className="text-muted-foreground text-sm">
                  Customize colors, position, and behavior to match your brand.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-brand-magenta/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                <span className="text-brand-magenta font-bold">2</span>
              </div>
              <div>
                <h4 className="text-card-foreground mb-1 font-medium">
                  Copy the code
                </h4>
                <p className="text-muted-foreground text-sm">
                  Go to the &quot;Code&quot; tab and copy the generated snippet.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-brand-purple/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                <span className="text-brand-purple font-bold">3</span>
              </div>
              <div>
                <h4 className="text-card-foreground mb-1 font-medium">
                  Paste in your site
                </h4>
                <p className="text-muted-foreground text-sm">
                  Insert the code before the &lt;/body&gt;&nbsp;tag in your
                  HTML.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Widget Type Selection */}
        <div className="bg-card border-border rounded-xl border p-6">
          <h3 className="text-card-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
            <Icon name="Layers" className="text-brand-cyan h-5 w-5" />
            Widget Type
          </h3>
          <div className="flex flex-col gap-4 md:flex-row">
            {WIDGET_TYPES.filter((widget) => widget.enabled).map((widget) => (
              <div key={widget.id} className="flex-1">
                <WidgetTypeCard
                  widget={widget}
                  isSelected={selectedType === widget.id}
                  onSelect={() => setSelectedType(widget.id)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-card border-border overflow-hidden rounded-xl border">
          <div className="border-border flex flex-col border-b sm:flex-row">
            <button
              onClick={() => setActiveTab("configure")}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "configure"
                  ? "bg-brand-gradient text-white"
                  : "text-muted-foreground hover:text-card-foreground hover:bg-accent"
              }`}
            >
              <Icon name="Settings" className="h-4 w-4" />
              1. Configure
            </button>
            <button
              onClick={() => setActiveTab("code")}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "code"
                  ? "bg-brand-gradient text-white"
                  : "text-muted-foreground hover:text-card-foreground hover:bg-accent"
              }`}
            >
              <Icon name="Code" className="h-4 w-4" />
              2. Code
            </button>
            <button
              onClick={() => setActiveTab("preview")}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "preview"
                  ? "bg-brand-gradient text-white"
                  : "text-muted-foreground hover:text-card-foreground hover:bg-accent"
              }`}
            >
              <Icon name="Eye" className="h-4 w-4" />
              Preview
            </button>
          </div>

          <div className="p-6">
            {activeTab === "configure" && (
              <WidgetConfigurator
                config={config}
                widgetType={selectedType}
                onChange={handleConfigChange}
              />
            )}
            {activeTab === "code" && (
              <WidgetCodeSnippet
                config={config}
                widgetType={selectedType}
                widgetName={selectedWidget?.name}
              />
            )}
            {activeTab === "preview" && (
              <WidgetPreview config={config} widgetType={selectedType} />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WidgetConfigModal;
